import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Palette, Sparkles, ShieldCheck, Timer, RefreshCw, KeyRound, Puzzle } from "lucide-react";

/**
 * The Settings sections, and the navigation between them.
 *
 * `SECTIONS` is the single source of truth: the router resolves `:section`
 * against it, this bar renders from it, and an unknown segment redirects to the
 * first entry. Adding a section means adding one row here and one component.
 *
 * Sub-routes rather than in-page tabs, deliberately. README and DEPLOYMENT both
 * tell an operator to go to "Settings > Branding", which is now a URL they can
 * link; the browser back button behaves; and each section loads only its own
 * data instead of the page fetching five endpoints on every visit.
 *
 * **Horizontal at every width, not a rail.** A vertical rail was the first cut
 * and it was wrong for this page on two counts. It sat immediately right of
 * Layout's own `w-64` sidebar, so the page opened with two stacked vertical navs
 * and no hierarchy between them; and it charged 228px of the content width for
 * seven items that never filled its height. That width is not spare here:
 * Layout gives `main` 960px at a 1280px viewport, so the rail left 732px for a
 * section, and Branding's `360px` sticky preview column then left its form 340px
 * -- while the form's own field rows split at `sm:grid-cols-2`, which is keyed
 * to the *viewport*, not the column. Two 155px-wide text inputs at 1280px was a
 * real defect, not a tight fit. Full width returns the form to 568px.
 *
 * One nav for all widths also replaces the rail-plus-mobile-chips pair, so
 * there is one active state to style instead of two.
 */

export const SECTIONS = [
  { key: "branding",     icon: Palette,     tone: "primary", labelKey: "settings.nav.branding" },
  { key: "ai",           icon: Sparkles,    tone: "glow",    labelKey: "settings.nav.ai" },
  { key: "permissions",  icon: ShieldCheck, tone: "success", labelKey: "settings.nav.permissions" },
  { key: "thresholds",   icon: Timer,       tone: "amber",   labelKey: "settings.nav.thresholds" },
  { key: "automation",   icon: RefreshCw,   tone: "amber",   labelKey: "settings.nav.automation" },
  { key: "api-keys",     icon: KeyRound,    tone: "glow",    labelKey: "settings.nav.apiKeys" },
  { key: "integrations", icon: Puzzle,      tone: "primary", labelKey: "settings.nav.integrations" },
];

export const DEFAULT_SECTION = SECTIONS[0].key;

export function findSection(key) {
  return SECTIONS.find((s) => s.key === key) || null;
}

export default function SettingsNav() {
  const { t } = useTranslation();

  return (
    // The negative margins match Layout's own `px-4 sm:px-6 lg:px-8`, so the
    // hairline runs the full content width and a scrolled row of tabs runs off
    // the edge of the screen rather than stopping short of it.
    //
    // Hungarian is the width case: its seven labels are ~885px against 960px of
    // `main` at a 1280px viewport, so the row fits but not by much, and
    // `overflow-x-auto` is the safety valve rather than a mobile-only feature.
    <nav aria-label={t("settings.title")} data-testid="settings-nav"
      className="-mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 border-b border-border overflow-x-auto">
      <ul className="flex items-center gap-1 w-max">
        {SECTIONS.map(({ key, icon: Icon, labelKey }) => (
          <li key={key}>
            <NavLink to={`/settings/${key}`} data-testid={`settings-nav-${key}`}
              className={({ isActive }) =>
                `group relative flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap transition-colors duration-200 ${
                  isActive ? "text-txt font-medium" : "text-muted hover:text-txt"
                }`}>
              {({ isActive }) => (
                <>
                  <Icon size={15} className={isActive ? "text-primary" : ""} />
                  <span>{t(labelKey)}</span>
                  {/* Sits at bottom-0, not -bottom-px: `overflow-x-auto`
                      forces overflow-y to compute to auto as well, so anything
                      hanging below the padding edge is clipped -- and would add
                      a vertical scrollbar to a horizontal bar. Flush with the
                      container's own border-b is the same look without that. */}
                  <span aria-hidden="true"
                    className={`absolute left-2 right-2 bottom-0 h-[2px] rounded-full transition-colors ${
                      isActive ? "bg-primary" : "bg-transparent"}`} />
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
